import React, { useState, useEffect } from "react";
import { FaPlus, FaEdit, FaTrash, FaBuilding, FaTimes, FaUserPlus } from "react-icons/fa";
import Swal from "sweetalert2";
import useAxios from "../utils/useAxios";
import { useSidebar } from "../context/SidebarContext";
import { useNavigate } from "react-router-dom";
import { urlimage } from '../utils/config';

const OrganisationPage = () => {
  const api = useAxios();
  const { sidebarVisible } = useSidebar();
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [formData, setFormData] = useState({
    nom: "",
    adresse: "",
    email: "",
    telephone: "",
    site_web: "",
    description: "",
    logo: null,
  });
  const navigate = useNavigate();

  const fetchOrganisations = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/organisations/");
      setOrganisations(data);
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: "Erreur",
        text: "Impossible de charger les organisations.",
        icon: "error",
        toast: true,
        position: "top-right",
        timer: 5000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganisations();
  }, []);

  const openModal = (org = null) => {
    if (org) {
      setIsEditing(true);
      setSelectedOrg(org.id);
      setFormData({
        nom: org.nom || "",
        adresse: org.adresse || "",
        email: org.email || "",
        telephone: org.telephone || "",
        site_web: org.site_web || "",
        description: org.description || "",
        logo: null,
      });
    } else {
      setIsEditing(false);
      setSelectedOrg(null);
      setFormData({
        nom: "",
        adresse: "",
        email: "",
        telephone: "",
        site_web: "",
        description: "",
        logo: null,
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "logo") {
      setFormData({ ...formData, logo: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formPayload = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value) formPayload.append(key, value);
    });

    try {
      if (isEditing && selectedOrg) {
        await api.put(`/organisations/${selectedOrg}/update/`, formPayload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        Swal.fire({
          title: "Organisation modifiée avec succès",
          icon: "success",
          toast: true,
          position: "top-right",
          timer: 5000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      } else {
        await api.post("/organisations/create/", formPayload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        Swal.fire({
          title: "Organisation ajoutée avec succès",
          icon: "success",
          toast: true,
          position: "top-right",
          timer: 5000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
      fetchOrganisations();
      closeModal();
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: "Erreur",
        text: "Impossible d'enregistrer l'organisation.",
        icon: "error",
        toast: true,
        position: "top-right",
        timer: 5000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Supprimer cette organisation ?",
      text: "Cette action est irréversible.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Oui, supprimer",
    });

    if (confirm.isConfirmed) {
      try {
        await api.delete(`/organisations/${id}/delete/`);
        setOrganisations((prev) => prev.filter((org) => org.id !== id));
        Swal.fire({
          title: "Supprimée !",
          text: "L'organisation a été supprimée.",
          icon: "success",
          toast: true,
          position: "top-right",
          timer: 5000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error(error);
        Swal.fire({
          title: "Erreur",
          text: "Impossible de supprimer l'organisation.",
          icon: "error",
          toast: true,
          position: "top-right",
          timer: 5000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    }
  };

  const styles = {
    container: { marginLeft: sidebarVisible ? 250 : 0, padding: 20, minHeight: "100vh", backgroundColor: "#f0f2f5", fontFamily: "'Inter', sans-serif" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 25 },
    title: { fontSize: 28, fontWeight: 700, color: "#1B019B", display: "flex", alignItems: "center", gap: 12 },
    addButton: { background: "#1B019B", color: "#fff", border: "none", padding: "12px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: 10, transition: "0.3s" },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 20 },
    card: { background: "#fff", padding: 20, borderRadius: 16, boxShadow: "0 8px 20px rgba(0,0,0,0.08)", display: "flex", flexDirection: "column", justifyContent: "space-between", transition: "0.3s", cursor: "pointer" },
    cardTitle: { fontSize: 20, fontWeight: 600, color: "#1B019B", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 },
    cardText: { color: "#555", marginBottom: 8, fontSize: 14 },
    cardActions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 15 },
    editBtn: { background: "#ffc107", border: "none", padding: "8px 12px", borderRadius: 8, cursor: "pointer", color: "#fff", fontWeight: 600 },
    deleteBtn: { background: "#dc3545", border: "none", padding: "8px 12px", borderRadius: 8, cursor: "pointer", color: "#fff", fontWeight: 600 },
    modalOverlay: { position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.45)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
    modalContent: { background: "#fff", borderRadius: 16, padding: 25, maxWidth: 500, width: "90%", position: "relative" },
    input: { width: "100%", padding: 12, borderRadius: 8, border: "1px solid #ccc", marginBottom: 15, fontSize: 14 },
    textarea: { width: "100%", padding: 12, borderRadius: 8, border: "1px solid #ccc", marginBottom: 15, fontSize: 14, minHeight: 80 },
    submitBtn: { background: "#1B019B", color: "#fff", border: "none", padding: 12, borderRadius: 8, width: "100%", cursor: "pointer", fontWeight: 600, fontSize: 16 },
    closeBtn: { position: "absolute", top: 15, right: 15, background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#555" },
    noData: { textAlign: "center", color: "#555", marginTop: 50, fontSize: 16 },
    adminBtn: { background: "#1B019B", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 600, marginTop: 15, transition: "0.3s" },
    logo: { width: 60, height: 60, objectFit: "cover", borderRadius: 8, marginBottom: 10 }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>
          <FaBuilding /> Gestion des Organisations
        </h3>
        <button
          style={styles.addButton}
          onClick={() => openModal()}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#2a0fdb")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#1B019B")}
        >
          <FaPlus /> Nouvelle Organisation
        </button>
      </div>

      {loading ? (
        <p style={styles.noData}>Chargement...</p>
      ) : organisations.length > 0 ? (
        <div style={styles.grid}>
          {organisations.map((org) => (
            <div
              key={org.id}
              style={styles.card}
              onMouseEnter={(e) => Object.assign(e.currentTarget.style, { transform: "translateY(-5px)", boxShadow: "0 12px 25px rgba(0,0,0,0.15)" })}
              onMouseLeave={(e) => Object.assign(e.currentTarget.style, { transform: "translateY(0)", boxShadow: "0 8px 20px rgba(0,0,0,0.08)" })}
            >
              <div>
                {org.logo && <img src={`${urlimage}${org.logo}`} alt={org.nom} style={styles.logo} />}

                <h4 style={styles.cardTitle}>
                  <FaBuilding /> {org.nom}
                </h4>
                <p style={styles.cardText}>Adresse: {org.adresse || "—"}</p>
                <p style={styles.cardText}>Email: {org.email || "—"}</p>
                <p style={styles.cardText}>Téléphone: {org.telephone || "—"}</p>
                <p style={styles.cardText}>Site Web: {org.site_web || "—"}</p>
                <p style={styles.cardText}>Créé le: {new Date(org.created_at).toLocaleDateString()}</p>
              </div>
              <div style={styles.cardActions}>
                <button style={styles.editBtn} onClick={() => openModal(org)}>
                  <FaEdit />
                </button>
                <button style={styles.deleteBtn} onClick={() => handleDelete(org.id)}>
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p style={styles.noData}>Aucune organisation trouvée.</p>
      )}

      <div style={{ textAlign: "center", marginTop: 40 }}>
        <p style={{ color: "#1B019B", fontSize: 16 }}>
          Veuillez ajouter un administrateur pour gérer l'organisation.
        </p>
        <button
          onClick={() => navigate("/superadmin")}
          style={styles.adminBtn}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#2a0fdb")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#1B019B")}
        >
          <FaUserPlus /> Ajouter un Administrateur
        </button>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <button onClick={closeModal} style={styles.closeBtn}><FaTimes /></button>
            <h4 style={{ textAlign: "center", marginBottom: 20, color: "#1B019B" }}>
              {isEditing ? "Modifier Organisation" : "Nouvelle Organisation"}
            </h4>
            <form onSubmit={handleSubmit}>
              <input type="text" name="nom" placeholder="Nom" style={styles.input} value={formData.nom} onChange={handleChange} required />
              <input type="text" name="adresse" placeholder="Adresse" style={styles.input} value={formData.adresse} onChange={handleChange} />
              <input type="email" name="email" placeholder="Email" style={styles.input} value={formData.email} onChange={handleChange} />
              <input type="text" name="telephone" placeholder="Téléphone" style={styles.input} value={formData.telephone} onChange={handleChange} />
              <input type="url" name="site_web" placeholder="Site Web" style={styles.input} value={formData.site_web} onChange={handleChange} />
              <textarea name="description" placeholder="Description" style={styles.textarea} value={formData.description} onChange={handleChange} />
              <input type="file" name="logo" accept="image/*" style={styles.input} onChange={handleChange} />
              <button type="submit" style={styles.submitBtn}>{isEditing ? "Mettre à jour" : "Créer"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganisationPage;
